import { orderBy } from 'lodash';

import { AllThreadsAndMessages, ThreadMessage } from '@missionlabs/smartagent-service-thread';

import { getThreads } from 'services/api/api.thread';

import { IEmailMessage, IEmailAttachment } from 'components/EmailMessage/types';

import ContactState from 'store/contact/contact.state';
import { channelTypes } from 'store/contactHistory/contactHistory.constants';

import { IInteraction, ChannelType, ContactHistory, InteractionMessage } from './types';
import { ETRType, IETR } from '@missionlabs/smartagent-lib-shared';
import { SocialChatJsonMessageType } from '@missionlabs/smartagent-service-chat/dist/types/socialChatJsonMessage';

export const isChannelType = (channel: string): channel is ChannelType => {
    return channelTypes[channel as ChannelType] !== undefined;
};

export const getChannelType = (contact: ContactState | null): ChannelType => {
    let channelType: ChannelType = 'UNKNOWN';

    if (!contact) return channelType;

    const channel = contact.channel;
    // Voice channels
    if (channel === 'VOICE') {
        const { initiationMethod: subChannel } = contact;
        const fullChannel = `${channel} ${subChannel}`;

        if (isChannelType(fullChannel)) channelType = fullChannel;
    }
    // Social and Webchat channels
    else if (channel === 'CHAT') {
        const {
            'sa-social-media-platform': socialChannel,
            'sa-sub-channel': socialSubChannel,
            'sa-initiation-method': initiationMethod,
        } = contact.attributes;
        if (socialChannel && socialSubChannel) {
            const subChannel =
                initiationMethod === 'outbound' || initiationMethod === 'contact_flow_initiated'
                    ? 'OUTBOUND'
                    : socialSubChannel;

            const fullChannel = `${socialChannel} ${subChannel}`;

            if (isChannelType(fullChannel)) channelType = fullChannel;
        } else {
            channelType = 'WEBCHAT';
        }
    }
    // Emails
    else if (channel === 'TASK') {
        channelType = 'TASK';
    }

    return channelType;
};

export const getThreadMessageGroups = async (contacts: ContactState[]): Promise<AllThreadsAndMessages[]> => {
    const threadIds = [
        ...new Set(
            contacts.map(contact => contact.attributes['sa-parent-thread-id'] || contact.attributes['sa-threadID'])
        ),
    ];

    const filteredThreadsIds = threadIds.filter(threadId => threadId); // filter out any undefined values from array

    return filteredThreadsIds.length > 0 ? await getThreads(filteredThreadsIds) : [];
};

export const transformTaskContacts = (
    contacts: ContactState[],
    threadMessageGroups: AllThreadsAndMessages[]
): IInteraction[] => {
    // Create interactions to represent every message found inside the thread message groups
    const interactions = threadMessageGroups
        .reduce((currentInteractions: IInteraction[], threadMessageGroup) => {
            const { subject, systemEndpointAddress, customerEndpointAddress, messages, hasAutoReply } =
                threadMessageGroup;

            // Transform each message in the group into an email message
            const transformedMessages = messages.map(message =>
                constructEmailMessage(subject, systemEndpointAddress, customerEndpointAddress, message)
            );

            // Order messages to read from newest to oldest
            const orderedMessages = orderBy(transformedMessages, 'timestamp', 'desc');

            // Iterate through messages to construct their corresponding interaction and add to the current interactions
            orderedMessages.forEach(message => {
                // Find the message's corresponding contact to act as the base of the interaction
                const matchingContact = contacts.find(contact => contact.ID === message.contactId);

                // Only construct the interaction if a matching contact has been found
                if (matchingContact) {
                    const { actionAuditId, timestamp, messageId, direction, agentName, type } = message;

                    const channelType: ChannelType =
                        direction === 'OUTBOUND'
                            ? 'TASK OUTBOUND'
                            : type === 'CCREPLY'
                            ? 'TASK CCREPLY'
                            : 'TASK INBOUND'

                    currentInteractions.push({
                        ...matchingContact,
                        agentName: matchingContact.agentName ?? agentName,
                        actionAuditId,
                        messageId: messageId,
                        emailMessages: orderedMessages,
                        viewPreviousEmailMessages: false,
                        connectedToAgentTimestamp: timestamp,
                        channelType,
                        hasAutoReply,
                        attributes: matchingContact.attributes,
                    } as IInteraction);
                }
            });

            return currentInteractions;
        }, [])
        // Filter to remove duplicate messages (affecting dev contacts from before the fix in SMAR-5716)
        .filter(({ messageId }, i, array) => array.map(int => int.messageId).indexOf(messageId) === i);

    // Order interactions from newest to oldest
    return orderBy(interactions, 'connectedToAgentTimestamp', 'desc');
};

export const transformContacts = (contacts: ContactState[]): IInteraction[] => {
    // Function to perform any needed transformations on the incoming interactions before being added to the contactHistory state
    return contacts.map(contact => {
        const { disconnectTimestamp, connectedToAgentTimestamp, attributes = {} } = contact;
        const interactionDuration =
            disconnectTimestamp && connectedToAgentTimestamp ? disconnectTimestamp - connectedToAgentTimestamp : 0;
        const channelType = getChannelType(contact);
        const agentName = contact.agentName ?? contact.attributes['sa-agent-name'];
        
        return { ...contact, interactionDuration, channelType, attributes, agentName } as IInteraction;
    });
};

export const transformSMSCampaignETRToInteraction = (etr: IETR): Partial<IInteraction> => {
    let channelType: ChannelType;
    let agentName: string;

    switch (etr.type) {
        case ETRType.SMS_CAMPAIGN_MESSAGE:
            channelType = channelTypes["SMS CAMPAIGN-MESSAGE"]
            agentName = etr.attributes["sa-agent-name"] || "CAMPAIGN"
            break
        case ETRType.SMS_MARKETING_MESSAGE:
            channelType = channelTypes["SMS MARKETING-MESSAGE"]
            agentName = "MARKETING"
            break
    }

    const transcript: (InteractionMessage | Partial<InteractionMessage>)[] | undefined = etr.content 
        ? [{
            Id: `${etr.ID}-message`,
            content: etr.content,
            AbsoluteTime: new Date(etr.timestamp).toISOString(),
            Type: SocialChatJsonMessageType.MESSAGE,
            DisplayName: 'SYSTEM_MESSAGE',
            ContentType: "text/plain",
            ParticipantRole: 'SYSTEM',
          }]
        : undefined;

    return {
        ID: etr.ID,
        channelType,
        initiationTimestamp: etr.timestamp,
        channel: "CHAT",
        attributes: etr.attributes,
        queueName: "N/A",
        agentName,
        connectedToSystemTimestamp: etr.timestamp,
        disconnectReason: "SYSTEM DISCONNECT",
        initiationMethod: "OUTBOUND",
        customerEndpointAddress: etr.attributes["sa-customer-endpoint-address"],
        systemEndpointAddress: etr.attributes["sa-system-endpoint-address"],
        attributesUpdated: true,
        transcript
    };
};


export const constructEmailMessage = (
    subject: string | undefined,
    systemEndpointAddress: string | undefined,
    customerEndpointAddress: string | undefined,
    message: ThreadMessage
): IEmailMessage => {
    const {
        messageID,
        direction,
        ccEmailAddress,
        actionAuditID,
        timestamp,
        content,
        type,
        contactID,
        attachments = [],
        externalFrom,
        forwardedTo,
        repliedTo,
        ccEndpointAddress
    } = message;

    const systemDisplayName = message.agent?.name || systemEndpointAddress;

    const displayedCc = ccEmailAddress || 'None found';
    const displayedSubject = subject || 'None found';
    const displayedFrom = (direction === 'OUTBOUND' ? systemDisplayName : (ccEndpointAddress ?? customerEndpointAddress)) ?? 'None found';
    const displayedTo = (direction === 'OUTBOUND' ? customerEndpointAddress : systemDisplayName) ?? 'None found';

    return {
        direction,
        actionAuditId: actionAuditID,
        contactId: contactID,
        messageId: messageID,
        timestamp,
        from: displayedFrom,
        to: displayedTo,
        subject: displayedSubject,
        content,
        type: ccEndpointAddress ? 'CCREPLY' : externalFrom ? 'EXTERNAL' : type,
        cc: displayedCc,
        attachments,
        externalFrom,
        forwardedTo,
        repliedTo,
    } as IEmailMessage;
};

export const attachmentsRequireTransformation = (attachments: IEmailAttachment[]): boolean => {
    return attachments.length > 0 && !attachments[0]?.hasOwnProperty('s3URL');
};

export const mergeAndMapData = (
    ctrData: ContactState[] | undefined,
    etrData: IETR[] | undefined
  ): ContactHistory | undefined => {
    if(!etrData && !ctrData) return;
    if(etrData) {
        const mappedETRData = etrData.map(transformSMSCampaignETRToInteraction);
        if(ctrData) {
            const mappedCTRData = transformContacts(ctrData)
            return [...mappedCTRData, ...mappedETRData].sort(
                (a, b) => b.initiationTimestamp! - a.initiationTimestamp!
            ) as ContactHistory;
        }
        return mappedETRData;
    }
    if(ctrData) {
        const ctrs = transformContacts(ctrData);
        return ctrs
    }
};
